import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { UserPlus, Mail, Loader2, ShieldAlert, ShieldCheck, Fingerprint, Lock, ArrowLeft } from 'lucide-react';
import './HubApp.css';

interface RegisterPortalProps {
    onBack: () => void;
}

/**
 * RegisterPortal: Genesis entry path for new account provisioning.
 * SHADOW VERSION (.tsx)
 */
export const RegisterPortal: React.FC<RegisterPortalProps> = ({ onBack }) => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { error } = await supabase.auth.signUp({
            email,
            password,
        });

        if (error) {
            setError(error.message);
            setLoading(false);
        } else {
            setSuccess(true);
            setLoading(false);
        }
    };

    return (
        <div className="hub-root viewport-constrained" style={{ maxWidth: '500px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflow: 'hidden' }}>
            <div className="hub-glow-orange" style={{ width: '60%', height: '60%', top: '-20%', left: '-20%', opacity: 0.15 }}></div>
            <div className="hub-glow-blue" style={{ width: '60%', height: '60%', bottom: '-20%', right: '-20%', opacity: 0.1 }}></div>
            
            <div className="animate-in fade-in zoom-in duration-700" style={{ width: '100%', position: 'relative', zIndex: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '-30px', position: 'relative', zIndex: 20 }}>
                    <div style={{ backgroundColor: '#000', padding: '20px', borderRadius: '50%', border: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)' }}>
                        <UserPlus size={40} color="#3b82f6" strokeWidth={1.5} />
                    </div>
                </div>

                <div className="premium-card" style={{ 
                    padding: '60px 30px 40px', 
                    textAlign: 'center', 
                    backgroundColor: 'rgba(10, 10, 10, 0.8)', 
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.8)'
                }}>
                    <header style={{ marginBottom: '40px' }}>
                        <h1 className="hub-title" style={{ fontSize: '28px', marginBottom: '8px', letterSpacing: '-1px' }}>
                            GENESIS <span>PORTAL</span>
                        </h1>
                        <div style={{ height: '1px', width: '40px', backgroundColor: '#3b82f6', margin: '0 auto 15px', opacity: 0.5 }}></div>
                        <p style={{ fontSize: '9px', fontWeight: '900', color: '#666', textTransform: 'uppercase', letterSpacing: '3px' }}>
                            Authorized Personnel Entry
                        </p>
                    </header>

                    {success ? (
                        <div className="animate-in zoom-in duration-500">
                            <div style={{ backgroundColor: 'rgba(46, 204, 113, 0.05)', border: '1px solid rgba(46, 204, 113, 0.2)', borderRadius: '12px', padding: '20px', marginBottom: '30px' }}>
                                <ShieldCheck size={48} color="#2ecc71" style={{ margin: '0 auto 15px' }} />
                                <h3 style={{ fontSize: '16px', fontWeight: '900', color: '#fff', marginBottom: '10px' }}>IDENTITY REGISTERED</h3>
                                <p style={{ fontSize: '12px', color: '#888', lineHeight: 1.5, margin: 0 }}>
                                    Check your transmission log (email) to verify your access credentials.
                                </p>
                            </div>
                            <button 
                                onClick={onBack}
                                className="premium-btn-primary" 
                                style={{ width: '100%', height: '54px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                            >
                                <ArrowLeft size={18} />
                                <span>RETURN TO LOGIN</span>
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="animate-in slide-in-from-top-2 duration-300" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '12px', padding: '15px', marginBottom: '30px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <ShieldAlert size={20} color="#ef4444" />
                                    <p style={{ fontSize: '11px', color: '#ef4444', fontWeight: 'bold', textAlign: 'left', margin: 0, lineHeight: 1.4 }}>{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div style={{ textAlign: 'left' }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: '#444', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Mail size={12} color="#3b82f6" /> New Athlete ID
                                    </label>
                                    <input 
                                        type="email" 
                                        className="premium-input" 
                                        style={{ 
                                            fontSize: '15px', 
                                            textAlign: 'left', 
                                            height: '54px', 
                                            padding: '0 20px', 
                                            backgroundColor: 'rgba(255,255,255,0.02)',
                                            borderRadius: '14px',
                                            border: '1px solid rgba(255,255,255,0.05)'
                                        }}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="new.athlete@masterplan.io"
                                        required
                                    />
                                </div>

                                <div style={{ textAlign: 'left' }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: '#444', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Lock size={12} color="#3b82f6" /> Set Security Phrase
                                    </label>
                                    <input 
                                        type="password" 
                                        className="premium-input" 
                                        style={{ 
                                            fontSize: '15px', 
                                            textAlign: 'left', 
                                            height: '54px', 
                                            padding: '0 20px', 
                                            backgroundColor: 'rgba(255,255,255,0.02)',
                                            borderRadius: '14px',
                                            border: '1px solid rgba(255,255,255,0.05)'
                                        }}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••••••"
                                        required
                                        minLength={6}
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className="premium-btn-primary" 
                                    style={{ 
                                        marginTop: '15px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: '12px',
                                        height: '60px',
                                        borderRadius: '16px',
                                        backgroundColor: '#3b82f6',
                                        boxShadow: '0 10px 30px rgba(59, 130, 246, 0.2)'
                                    }}
                                >
                                    {loading ? <Loader2 className="animate-spin" size={20} /> : (
                                        <>
                                            <Fingerprint size={20} />
                                            <span>PROVISION ACCOUNT</span>
                                        </>
                                    )}
                                </button>
                            </form>

                            <button 
                                onClick={onBack}
                                style={{ all: 'unset', marginTop: '25px', fontSize: '10px', fontWeight: '900', color: '#444', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }}
                            >
                                Cancel Provisioning
                            </button>
                        </>
                    )}

                    <div style={{ marginTop: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                        <div style={{ height: '1px', flex: 1, backgroundColor: 'rgba(255,255,255,0.03)' }}></div>
                        <p style={{ fontSize: '8px', fontWeight: '900', color: '#222', textTransform: 'uppercase', letterSpacing: '5px', margin: 0 }}>
                            MASTER PLAN CORE V2.1
                        </p>
                        <div style={{ height: '1px', flex: 1, backgroundColor: 'rgba(255,255,255,0.03)' }}></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
